// ABOUTME: Preview part of the bulk change's third step: scan progress, summary, status chips, filters and the rows table.
// ABOUTME: Lets the user exclude rows (one by one or by filter) and confirm the apply of the remaining changes (or deletes).

import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Checkbox,
    Dialog,
    Divider,
    FormControlLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@ds/coolshop';
import { CellContext } from '@tanstack/react-table';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useDeleteImpact } from '../hooks/useDeleteImpact';
import { useFindReplaceExclusions } from '../hooks/useFindReplaceExclusions';
import { MAX_ROWS_PAGE_SIZE, useFindReplaceRows } from '../hooks/useFindReplaceRows';
import { useDroppedReasonLabel, useSkipReasonLabel } from '../hooks/useReasonLabels';
import {
    DeleteNodeAction,
    FindReplaceRowDto,
    FindReplaceRunDto,
    isDeleteRun,
    RowsFilter,
    RowStatus,
} from '../types';
import { actionKindMessages } from '../utils/actionMessages';
import { contentTypeKey, ContentTypeLabeler } from '../utils/contentTypeLabels';
import { DeleteImpactMessage } from './DeleteImpactMessage';
import { FilterChip } from './FilterChip';
import { FindReplaceRowsTable, RowColumn } from './FindReplaceRowsTable';
import { InfoNote } from './InfoNote';
import { JobProgress } from './JobProgress';
import { StepFooter } from './StepFooter';

const SEARCH_DEBOUNCE_MS = 300;

/** The value of the "All …" entry of the row filters (an empty value would render an empty select). */
export const ALL = '*';

/** The inclusion state and handlers the checkbox column reads (its cells render outside this step). */
type InclusionContextValue = {
    pending: boolean;
    allIncluded: boolean;
    someExcluded: boolean;
    hasRows: boolean;
    onToggleAll: () => void;
    onToggleRow: (row: FindReplaceRowDto) => void;
};

const InclusionContext = createContext<InclusionContextValue | null>(null);

function IncludeAllHeader() {
    const inclusion = useContext(InclusionContext);
    if (!inclusion) return null;
    return (
        <Checkbox
            id="find-replace-include-all"
            data-testid="find-replace-include-all"
            checked={inclusion.allIncluded}
            indeterminate={inclusion.someExcluded}
            disabled={inclusion.pending || !inclusion.hasRows}
            onChange={inclusion.onToggleAll}
        />
    );
}

function IncludeRowCell({ row }: CellContext<FindReplaceRowDto, unknown>) {
    const inclusion = useContext(InclusionContext);
    if (!inclusion) return null;
    return (
        <Checkbox
            id={`find-replace-include-${row.original.seq}`}
            data-testid={`find-replace-row-checkbox-${row.original.seq}`}
            checked={!row.original.excluded}
            disabled={inclusion.pending}
            onChange={() => inclusion.onToggleRow(row.original)}
        />
    );
}

const INCLUDE_COLUMNS: RowColumn[] = [
    { id: 'include', size: 48, header: IncludeAllHeader, cell: IncludeRowCell },
];
const NO_COLUMNS: RowColumn[] = [];

type PreviewStepProps = {
    run: FindReplaceRunDto;
    hierarchyId: string;
    labeler: ContentTypeLabeler;
    defaultPageSize: number;
    onRunChanged: () => void;
    onCancel: (runId: string) => Promise<void>;
    onApply: (runId: string, reapprovePublish: boolean) => Promise<void>;
    /** Back to the Actions step: discards this preview. */
    onEditActions: () => void;
};

const errorMessage = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function PreviewStep({
    run,
    hierarchyId,
    labeler,
    defaultPageSize,
    onRunChanged,
    onCancel,
    onApply,
    onEditActions,
}: PreviewStepProps) {
    const intl = useIntl();
    const { formatMessage } = intl;
    const skipReasonLabel = useSkipReasonLabel();
    const droppedReasonLabel = useDroppedReasonLabel();

    const [status, setStatus] = useState<RowStatus>('change');
    const [actionIndex, setActionIndex] = useState(ALL);
    const [contentType, setContentType] = useState(ALL);
    const [lang, setLang] = useState(ALL);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [paging, setPaging] = useState({ page: 0, pageSize: defaultPageSize });
    const [reapprovePublish, setReapprovePublish] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);

    useEffect(() => {
        const timeout = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timeout);
    }, [search]);

    useEffect(() => {
        setPaging(prev => ({ ...prev, page: 0 }));
    }, [status, actionIndex, contentType, lang, debouncedSearch]);

    const isReady = run.status === 'ready';
    const { actions } = run.input;
    // A delete run has one action and no field or language: those filters and re-approval do not apply.
    const deleteAction = isDeleteRun(run.input) ? (actions[0] as DeleteNodeAction) : null;
    const deleteImpact = useDeleteImpact(confirmOpen && deleteAction ? run.id : null);

    const filter = useMemo<RowsFilter>(() => {
        const [ctg, ct] = contentType !== ALL ? contentType.split('/') : [];
        return {
            status,
            ...(actionIndex !== ALL ? { actionIndex: Number(actionIndex) } : {}),
            ...(ctg ? { ctg, ct } : {}),
            ...(lang !== ALL ? { lang } : {}),
            ...(debouncedSearch ? { q: debouncedSearch } : {}),
        };
    }, [status, actionIndex, contentType, lang, debouncedSearch]);

    const activeRunId = isReady ? run.id : null;
    const rowsQuery = useFindReplaceRows(activeRunId, filter, {
        skip: paging.page * paging.pageSize,
        limit: paging.pageSize,
    });
    // How many rows under the current filters are excluded: drives the header checkbox state.
    const excludedQuery = useFindReplaceRows(
        status === 'change' ? activeRunId : null,
        { ...filter, excluded: true },
        { skip: 0, limit: 1 },
    );
    const { setExcludedBySeqs, setExcludedByFilter, pending } =
        useFindReplaceExclusions(activeRunId);

    const { refetch: refetchRows } = rowsQuery;
    const { refetch: refetchExcluded } = excludedQuery;

    const runExclusion = useCallback(
        async (action: () => Promise<number>) => {
            setActionError(null);
            try {
                await action();
            } catch (e) {
                setActionError(errorMessage(e));
            } finally {
                refetchRows();
                refetchExcluded();
                onRunChanged();
            }
        },
        [refetchRows, refetchExcluded, onRunChanged],
    );

    const { summary } = run;
    const changeCount = summary.byStatus.change - summary.excluded;

    const hasRows = rowsQuery.count > 0;
    const allIncluded = hasRows && excludedQuery.count === 0;
    const someExcluded = excludedQuery.count > 0 && excludedQuery.count < rowsQuery.count;

    const inclusion = useMemo<InclusionContextValue>(
        () => ({
            pending,
            allIncluded,
            someExcluded,
            hasRows,
            // Ticked when nothing is excluded: unticking excludes every row of the filters
            onToggleAll: () => runExclusion(() => setExcludedByFilter(filter, allIncluded)),
            onToggleRow: row => runExclusion(() => setExcludedBySeqs([row.seq], !row.excluded)),
        }),
        [
            pending,
            allIncluded,
            someExcluded,
            hasRows,
            runExclusion,
            setExcludedByFilter,
            setExcludedBySeqs,
            filter,
        ],
    );

    const trailingColumns = useMemo<RowColumn[]>(
        () =>
            status === 'skipped'
                ? [
                      {
                          id: 'skipReason',
                          header: formatMessage({
                              id: 'findReplace.table.skipReason',
                              defaultMessage: 'Reason',
                          }),
                          cell: ({ row }) =>
                              row.original.skipReason
                                  ? skipReasonLabel(
                                        row.original.skipReason,
                                        row.original.skipDetail,
                                    )
                                  : '',
                      },
                  ]
                : NO_COLUMNS,
        [status, formatMessage, skipReasonLabel],
    );

    const handleCancelScan = async () => {
        setBusy(true);
        setActionError(null);
        try {
            await onCancel(run.id);
        } catch (e) {
            setActionError(errorMessage(e));
        } finally {
            setBusy(false);
            onRunChanged();
        }
    };

    const handleApply = async () => {
        setBusy(true);
        setActionError(null);
        try {
            await onApply(run.id, reapprovePublish);
            setConfirmOpen(false);
        } catch (e) {
            setConfirmOpen(false);
            setActionError(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    if (run.status === 'scanning') {
        return (
            <Stack gap={2} p={3}>
                <Typography variant="body1">
                    {formatMessage({
                        id: 'findReplace.preview.scanning',
                        defaultMessage: 'Computing the preview…',
                    })}
                </Typography>
                <JobProgress job={run.job} />
                {actionError && <Alert severity="error">{actionError}</Alert>}
                <Box>
                    <Button variant="text" onClick={handleCancelScan} disabled={busy}>
                        {formatMessage({
                            id: 'findReplace.preview.cancelScan',
                            defaultMessage: 'Cancel',
                        })}
                    </Button>
                </Box>
            </Stack>
        );
    }

    if (!isReady) {
        return (
            <Stack gap={2} p={3}>
                {run.status === 'too_many_matches' && (
                    <Alert severity="warning">
                        {formatMessage({
                            id: 'findReplace.preview.tooManyMatches',
                            defaultMessage:
                                'Too many values would change. Narrow the scope, the fields or the languages and compute the preview again.',
                        })}
                    </Alert>
                )}
                {run.status === 'failed' && (
                    <Alert severity="error">
                        {formatMessage(
                            {
                                id: 'findReplace.preview.failed',
                                defaultMessage: 'The preview failed: {error}',
                            },
                            { error: run.scanInfo?.error ?? '' },
                        )}
                    </Alert>
                )}
                {run.status === 'cancelled' && (
                    <Alert severity="info">
                        {formatMessage({
                            id: 'findReplace.preview.cancelled',
                            defaultMessage: 'The preview was cancelled.',
                        })}
                    </Alert>
                )}
                <Box>
                    <Button variant="contained" onClick={onEditActions}>
                        {formatMessage({
                            id: 'bulkChange.preview.editActions',
                            defaultMessage: 'Edit actions',
                        })}
                    </Button>
                </Box>
            </Stack>
        );
    }

    const dropped = run.scanInfo?.droppedContentTypes ?? [];
    const outOfScopeInherited = run.scanInfo?.outOfScopeInherited ?? 0;
    const skippedTotal = summary.byStatus.skipped;
    // No row at all: every object in scope already has the values the actions would write.
    const nothingToChange =
        summary.byStatus.change + summary.byStatus.skipped + summary.byStatus.invalid === 0;

    const statusChip = (value: RowStatus, label: string) => (
        <FilterChip
            key={value}
            label={label}
            selected={status === value}
            onClick={() => setStatus(value)}
            data-testid={`bulk-change-status-${value}`}
        />
    );

    const scopeCount = run.scanInfo?.scopeCount;
    const subtitle = deleteAction
        ? formatMessage({
              id: 'bulkChange.preview.subtitleDelete',
              defaultMessage: 'nothing is deleted until you apply',
          })
        : formatMessage(
              {
                  id: 'bulkChange.preview.subtitle',
                  defaultMessage:
                      '{actions, plural, one {# action} other {# actions}} · values are written only when you apply',
              },
              { actions: actions.length },
          );

    return (
        <Stack flex={1} minHeight={0}>
            <Stack gap={2} p={3} flex={1} minHeight={0} overflow="auto">
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Stack gap={0.5}>
                        <Typography variant="subtitle1" data-testid="find-replace-summary">
                            <Box component="span" fontWeight="bold">
                                {deleteAction
                                    ? formatMessage(
                                          {
                                              id: 'bulkChange.preview.summaryDelete',
                                              defaultMessage:
                                                  '{nodes, plural, one {# object} other {# objects}} to delete',
                                          },
                                          { nodes: summary.byStatus.change },
                                      )
                                    : formatMessage(
                                          {
                                              id: 'bulkChange.preview.summary',
                                              defaultMessage:
                                                  '{changes, plural, one {# change} other {# changes}} in {nodes, plural, one {# object} other {# objects}}',
                                          },
                                          {
                                              changes: summary.byStatus.change,
                                              nodes: run.nodeCount,
                                          },
                                      )}
                            </Box>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {scopeCount === undefined
                                ? subtitle
                                : `${formatMessage(
                                      {
                                          id: 'bulkChange.preview.scanned',
                                          defaultMessage:
                                              '{count, plural, one {# object scanned} other {# objects scanned}}',
                                      },
                                      { count: scopeCount },
                                  )} · ${subtitle}`}
                        </Typography>
                    </Stack>
                    <Button variant="text" onClick={onEditActions} disabled={busy}>
                        {formatMessage({
                            id: 'bulkChange.preview.editActions',
                            defaultMessage: 'Edit actions',
                        })}
                    </Button>
                </Stack>

                <Stack direction="row" gap={1}>
                    {statusChip(
                        'change',
                        formatMessage(
                            {
                                id: 'findReplace.preview.tab.changes',
                                defaultMessage: 'Changes ({count})',
                            },
                            { count: summary.byStatus.change },
                        ),
                    )}
                    {statusChip(
                        'skipped',
                        formatMessage(
                            {
                                id: 'findReplace.preview.tab.skipped',
                                defaultMessage: 'Skipped ({count})',
                            },
                            { count: summary.byStatus.skipped },
                        ),
                    )}
                    {statusChip(
                        'invalid',
                        formatMessage(
                            {
                                id: 'findReplace.preview.tab.invalid',
                                defaultMessage: 'Invalid ({count})',
                            },
                            { count: summary.byStatus.invalid },
                        ),
                    )}
                </Stack>

                {skippedTotal > 0 && (
                    <Alert severity="warning" data-testid="bulk-change-skipped-alert">
                        <AlertTitle>
                            {deleteAction
                                ? formatMessage(
                                      {
                                          id: 'bulkChange.preview.skippedTitleDelete',
                                          defaultMessage:
                                              '{count, plural, one {# object will not be deleted} other {# objects will not be deleted}}',
                                      },
                                      { count: skippedTotal },
                                  )
                                : formatMessage(
                                      {
                                          id: 'bulkChange.preview.skippedTitle',
                                          defaultMessage:
                                              '{count, plural, one {# value} other {# values}} will not be written',
                                      },
                                      { count: skippedTotal },
                                  )}
                        </AlertTitle>
                        {formatMessage(
                            {
                                id: 'bulkChange.preview.skippedDetail',
                                defaultMessage: '{reasons}. They are listed under Skipped.',
                            },
                            {
                                reasons: summary.bySkipReason
                                    .map(
                                        ({ reason, count }) =>
                                            `${count} · ${skipReasonLabel(reason)}`,
                                    )
                                    .join(', '),
                            },
                        )}
                    </Alert>
                )}

                {(dropped.length > 0 || outOfScopeInherited > 0) && (
                    <Alert severity="info">
                        <Stack gap={0.5}>
                            {dropped.map(({ ctg, ct, reason }) => (
                                <span key={contentTypeKey(ctg, ct)}>
                                    {formatMessage(
                                        {
                                            id: 'bulkChange.preview.droppedContentType',
                                            defaultMessage: '{contentType} was left out: {reason}.',
                                        },
                                        {
                                            contentType: `${labeler.groupLabel(
                                                ctg,
                                            )} › ${labeler.typeLabel(ctg, ct)}`,
                                            reason: droppedReasonLabel(reason),
                                        },
                                    )}
                                </span>
                            ))}
                            {outOfScopeInherited > 0 && (
                                <span>
                                    {formatMessage(
                                        {
                                            id: 'findReplace.preview.outOfScopeInherited',
                                            defaultMessage:
                                                '{count, plural, one {# matching value is} other {# matching values are}} inherited from nodes above the starting node and will not change.',
                                        },
                                        { count: outOfScopeInherited },
                                    )}
                                </span>
                            )}
                        </Stack>
                    </Alert>
                )}

                {nothingToChange ? (
                    <Box data-testid="bulk-change-nothing-to-change">
                        <InfoNote
                            title={formatMessage({
                                id: 'bulkChange.preview.nothingToChange.title',
                                defaultMessage: 'Nothing to change',
                            })}
                            text={formatMessage(
                                {
                                    id: 'bulkChange.preview.nothingToChange.text',
                                    defaultMessage:
                                        'The {count, plural, one {object} other {# objects}} in scope already {count, plural, one {has} other {have}} the values these actions would write.',
                                },
                                { count: scopeCount ?? 0 },
                            )}
                        />
                    </Box>
                ) : (
                    <>
                        <Stack direction="row" gap={2} alignItems="flex-end">
                            {!deleteAction && (
                                <>
                                    <Box width={200}>
                                        <Select
                                            label={formatMessage({
                                                id: 'bulkChange.filter.action',
                                                defaultMessage: 'Action',
                                            })}
                                            value={actionIndex}
                                            onChange={e => setActionIndex(e.target.value as string)}
                                            size="small"
                                            fullWidth
                                        >
                                            <MenuItem value={ALL}>
                                                {formatMessage({
                                                    id: 'bulkChange.filter.allActions',
                                                    defaultMessage: 'All actions',
                                                })}
                                            </MenuItem>
                                            {actions.map((action, index) => (
                                                // eslint-disable-next-line react/no-array-index-key
                                                <MenuItem key={index} value={`${index}`}>
                                                    {`${index + 1} · ${formatMessage(
                                                        actionKindMessages[action.kind],
                                                    )}${
                                                        action.kind === 'deleteNode'
                                                            ? ''
                                                            : ` · ${labeler.typeLabel(
                                                                  action.ctg,
                                                                  action.ct,
                                                              )}`
                                                    }`}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Box>
                                    <Box width={200}>
                                        <Select
                                            label={formatMessage({
                                                id: 'findReplace.filter.contentType',
                                                defaultMessage: 'Field',
                                            })}
                                            value={contentType}
                                            onChange={e => setContentType(e.target.value as string)}
                                            size="small"
                                            fullWidth
                                        >
                                            <MenuItem value={ALL}>
                                                {formatMessage({
                                                    id: 'findReplace.filter.allContentTypes',
                                                    defaultMessage: 'All fields',
                                                })}
                                            </MenuItem>
                                            {summary.byContentType.map(({ ctg, ct, count }) => (
                                                <MenuItem
                                                    key={contentTypeKey(ctg, ct)}
                                                    value={contentTypeKey(ctg, ct)}
                                                >
                                                    {`${labeler.typeLabel(ctg, ct)} (${count})`}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Box>
                                    <Box width={180}>
                                        <Select
                                            label={formatMessage({
                                                id: 'findReplace.filter.locale',
                                                defaultMessage: 'Language',
                                            })}
                                            value={lang}
                                            onChange={e => setLang(e.target.value as string)}
                                            size="small"
                                            fullWidth
                                        >
                                            <MenuItem value={ALL}>
                                                {formatMessage({
                                                    id: 'findReplace.filter.allLocales',
                                                    defaultMessage: 'All languages',
                                                })}
                                            </MenuItem>
                                            {summary.byLang.map(({ lang: code, count }) => (
                                                <MenuItem key={code} value={code}>
                                                    {`${labeler.languageLabel(code)} (${count})`}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Box>
                                </>
                            )}
                            <Box flex={1} minWidth={0}>
                                <TextField
                                    label={formatMessage({
                                        id: 'findReplace.filter.nodeCode',
                                        defaultMessage: 'Node code',
                                    })}
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    size="small"
                                    fullWidth
                                />
                            </Box>
                        </Stack>

                        {(actionError || rowsQuery.error) && (
                            <Alert severity="error">
                                {actionError ?? rowsQuery.error?.message}
                            </Alert>
                        )}

                        <Box border={1} borderColor="divider" borderRadius={2} overflow="hidden">
                            <InclusionContext.Provider value={inclusion}>
                                <FindReplaceRowsTable
                                    id={`find-replace-preview-${status}`}
                                    hierarchyId={hierarchyId}
                                    rows={rowsQuery.rows}
                                    count={rowsQuery.count}
                                    loading={rowsQuery.loading}
                                    page={paging.page}
                                    pageSize={paging.pageSize}
                                    onPageChange={(page, pageSize) =>
                                        setPaging({
                                            page,
                                            pageSize: Math.min(pageSize, MAX_ROWS_PAGE_SIZE),
                                        })
                                    }
                                    actions={actions}
                                    labeler={labeler}
                                    leadingColumns={
                                        status === 'change' ? INCLUDE_COLUMNS : NO_COLUMNS
                                    }
                                    trailingColumns={trailingColumns}
                                    emptyMessage={
                                        deleteAction
                                            ? formatMessage({
                                                  id: 'bulkChange.table.emptyDelete',
                                                  defaultMessage: 'No objects match these filters.',
                                              })
                                            : formatMessage({
                                                  id: 'findReplace.table.empty',
                                                  defaultMessage: 'No values match these filters.',
                                              })
                                    }
                                />
                            </InclusionContext.Provider>
                        </Box>

                        <Stack direction="row" justifyContent="space-between">
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                data-testid="bulk-change-selected"
                            >
                                {deleteAction
                                    ? formatMessage(
                                          {
                                              id: 'bulkChange.preview.selectedDelete',
                                              defaultMessage:
                                                  '{selected, number} of {total, number} objects selected · unticked objects are not deleted',
                                          },
                                          { selected: changeCount, total: summary.byStatus.change },
                                      )
                                    : formatMessage(
                                          {
                                              id: 'bulkChange.preview.selected',
                                              defaultMessage:
                                                  '{selected, number} of {total, number} changes selected · unticked values are left as they are',
                                          },
                                          { selected: changeCount, total: summary.byStatus.change },
                                      )}
                            </Typography>
                        </Stack>
                    </>
                )}

                {run.canReapprove && !deleteAction && (
                    <Stack gap={0.25}>
                        <Box pb={2}>
                            <Divider />
                        </Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    id="find-replace-reapprove"
                                    checked={reapprovePublish}
                                    onChange={e => setReapprovePublish(e.target.checked)}
                                />
                            }
                            label={formatMessage({
                                id: 'findReplace.preview.reapprovePublish',
                                defaultMessage:
                                    'Re-approve and publish the groups that were approved or published',
                            })}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {formatMessage({
                                id: 'bulkChange.preview.reapproveHint',
                                defaultMessage:
                                    'Only the groups you are allowed to approve. The others go back to draft and wait for an approver.',
                            })}
                        </Typography>
                    </Stack>
                )}
            </Stack>
            <StepFooter>
                <Button variant="text" onClick={onEditActions} disabled={busy}>
                    {formatMessage({ id: 'bulkChange.back', defaultMessage: 'Back' })}
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    data-testid="find-replace-apply-button"
                    onClick={() => setConfirmOpen(true)}
                    disabled={changeCount === 0 || busy}
                >
                    {deleteAction
                        ? formatMessage(
                              {
                                  id: 'bulkChange.preview.delete',
                                  defaultMessage:
                                      'Delete {count, plural, one {# object} other {# objects}}',
                              },
                              { count: changeCount },
                          )
                        : formatMessage(
                              {
                                  id: 'findReplace.preview.apply',
                                  defaultMessage:
                                      'Apply {count, plural, one {# change} other {# changes}}',
                              },
                              { count: changeCount },
                          )}
                </Button>
            </StepFooter>

            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                maxWidth="sm"
                fullWidth
                header={{
                    title: deleteAction
                        ? formatMessage({
                              id: 'bulkChange.confirmDelete.title',
                              defaultMessage: 'Delete the objects?',
                          })
                        : formatMessage({
                              id: 'findReplace.confirm.title',
                              defaultMessage: 'Apply the changes?',
                          }),
                    onClose: () => setConfirmOpen(false),
                }}
                actions={[
                    {
                        label: formatMessage({
                            id: 'findReplace.confirm.cancel',
                            defaultMessage: 'Back',
                        }),
                        variant: 'text',
                        onClick: () => setConfirmOpen(false),
                    },
                    {
                        label: deleteAction
                            ? formatMessage({
                                  id: 'bulkChange.confirmDelete.delete',
                                  defaultMessage: 'Delete',
                              })
                            : formatMessage({
                                  id: 'findReplace.confirm.apply',
                                  defaultMessage: 'Apply',
                              }),
                        variant: 'contained',
                        // A delete is confirmed only once what it removes is known.
                        disabled:
                            busy ||
                            (!!deleteAction && (!deleteImpact.impact || !!deleteImpact.error)),
                        loading: busy,
                        onClick: handleApply,
                        'data-testid': 'find-replace-apply-confirm',
                    },
                ]}
            >
                <Box p={3}>
                    {deleteAction ? (
                        <Box data-testid="bulk-change-confirm-delete">
                            <DeleteImpactMessage
                                impact={deleteImpact.impact}
                                loading={deleteImpact.loading}
                                error={deleteImpact.error}
                                linkedAction={deleteAction.linkedAction}
                            />
                        </Box>
                    ) : (
                        <Typography variant="body1">
                            {formatMessage(
                                {
                                    id: 'findReplace.confirm.message',
                                    defaultMessage:
                                        '{changes, plural, one {# value} other {# values}} in {nodes, plural, one {# object} other {# objects}} will change. This cannot be undone.',
                                },
                                { changes: changeCount, nodes: run.nodeCount },
                            )}
                        </Typography>
                    )}
                </Box>
            </Dialog>
        </Stack>
    );
}
